import {
  FormioComponent,
  FormioExtendedComponentSchema,
  FormioFormBuilderConfig,
} from '~/types/forms';

interface FormioColumn {
  components: Record<string, string | number | boolean | object>[];
  width: string;
  offset: number;
  push: number;
  pull: number;
  size: string;
}

export interface FormioCustomNavButtons {
  input: boolean;
  tableView: boolean;
  key: string;
  type: string;
  hideLabel: boolean;
  columns: FormioColumn[];
  builder: {
    onlyOnce: boolean;
    initialEdit: boolean;
    disableEdit: boolean;
    disableRemove: boolean;
    disableDrag: boolean;
    disableDrop: boolean;
    isMandatory: boolean;
  };
}
const customButton = (
  event: string,
  key: string,
  label: string,
  theme = 'primary',
) => {
  return {
    components: [
      {
        input: true,
        label,
        tableView: false,
        key,
        block: true,
        action: 'event',
        theme,
        type: 'button',
        event,
        hideOnChildrenHidden: false,
        builder: {
          onlyOnce: true,
          initialEdit: false,
          disableEdit: true,
          disableRemove: true,
          disableDrag: true,
          disableDrop: false,
          isMandatory: true,
        },
      },
    ],
    width: 'auto',
    offset: 0,
    push: 0,
    pull: 0,
    size: 'md',
  };
};

export const formioNavButtons = (
  identifier: string,
  previous: boolean,
  next: boolean,
  submit = false,
  cancel = true,
) => {
  const columns = [];

  if (cancel) {
    columns.push(
      customButton(
        'cancel',
        `navButtonCancel${identifier}`,
        'fCancel',
        'secondary',
      ),
    );
  }
  if (previous) {
    columns.push(
      customButton(
        'goToPreviousPage',
        `navButtonPrev${identifier}`,
        'fPrevious',
      ),
    );
  }
  if (next) {
    columns.push(
      customButton('goToNextPage', `navButtonNext${identifier}`, 'fNext'),
    );
  }
  if (submit) {
    columns.push(
      customButton('customSubmit', `navButtonSubmit${identifier}`, 'fSubmit'),
    );
  }

  return {
    input: false,
    tableView: false,
    key: `navColumnsPage${identifier}`,
    type: 'columns',
    hideLabel: true,
    columns,
    builder: {
      onlyOnce: true,
      initialEdit: false,
      disableEdit: true,
      disableRemove: true,
      disableDrag: true,
      disableDrop: false,
      isMandatory: true,
    },
  };
};

export const removeNavButtonSettings = (body: FormioFormBuilderConfig) => {
  body?.components?.forEach((comp) => {
    if (!comp?.components?.length) {
      return comp;
    }
    const lastComponetIndex = comp.components.length - 1;
    if (comp.components[lastComponetIndex]?.key?.startsWith('navColumnsPage')) {
      comp.components.pop();
    }
  });

  return body;
};

export const disableReadOnlyFields = (config: FormioFormBuilderConfig) => {
  const temp = (config.components || [])[0]?.components?.map(
    (panelComponents) => {
      return {
        ...panelComponents,
        disabled: panelComponents.key !== 'navColumnsPage1',
      };
    },
  );
  if (config && config.components) {
    config.components[0].components = temp;
  }

  return config;
};

export const disableReadOnlyRehireFields = (
  config: FormioFormBuilderConfig,
) => {
  const temp = (config.components || [])[0]?.components?.map(
    (panelComponents) => {
      return {
        ...panelComponents,
        disabled: panelComponents.key === 'navColumnsPage1' ? false : true,
      };
    },
  );
  if (config && config.components) {
    config.components[0].components = temp;
  }

  return config;
};

export const addCustomNavButtonSettings = (body: FormioFormBuilderConfig) => {
  const formNumberOfPages = body?.components?.length || 0;
  const updatedComponentSettings = body?.components?.map((comp, idx) => {
    const returnNavUpdatedComponent = (navButtons: FormioCustomNavButtons) => {
      return {
        ...comp,
        buttonSettings: {
          previous: false,
          cancel: false,
          next: false,
          submit: false,
        },
        components: [...(comp.components || []), navButtons],
      };
    };

    if (formNumberOfPages === 1 && idx === 0) {
      const customNavButtons = formioNavButtons('1', false, false, true, true);
      return returnNavUpdatedComponent(customNavButtons);
    } else if (formNumberOfPages > 1 && idx === 0) {
      const customNavButtons = formioNavButtons('1', false, true, false, true);
      return returnNavUpdatedComponent(customNavButtons);
    } else if (formNumberOfPages > 1 && idx + 1 < formNumberOfPages) {
      const customNavButtons = formioNavButtons(
        `${idx + 1}`,
        true,
        true,
        false,
        true,
      );
      return returnNavUpdatedComponent(customNavButtons);
    } else {
      const customNavButtons = formioNavButtons(
        `${idx + 1}`,
        true,
        false,
        true,
        true,
      );
      return returnNavUpdatedComponent(customNavButtons);
    }
  });
  return { ...body, components: updatedComponentSettings };
};

export const removeCustomDataPage = (config: FormioFormBuilderConfig) => {
  const index =
    config?.components?.findIndex((comp) => comp.key === 'customData') || -1;
  if (index > -1) {
    config?.components?.splice(index, 1);
  }
};

export const addCustomDataPage = (
  config: FormioFormBuilderConfig,
  components: FormioExtendedComponentSchema[],
) => {
  const customDataPageExists = config?.components?.find(
    (comp) => comp.key === 'customData',
  );
  if (!customDataPageExists) {
    return;
  }
  removeCustomDataPage(config);

  const length = config?.components?.length || 0;
  const componentsIndexed = components.reduce((acc, comp) => {
    // Handle Finnish characters in custom field terms
    const matchFinnishXters = /[åäöÅÄÖ]/g;
    const newKey = comp?.key?.replace(matchFinnishXters, '') || '';
    const normalizedNewKey = normalizeString(newKey).replace(
      /[^A-Za-z0-9_]/g,
      '',
    );
    return { ...acc, [newKey]: comp, [normalizedNewKey]: comp };
  }, {} as Record<string, FormioExtendedComponentSchema>);

  const customDataPage = {
    title: customDataPageExists.title || 'Custom data',
    breadcrumbClickable: true,
    navigateOnEnter: false,
    saveOnEnter: false,
    scrollToTop: false,
    type: 'panel',
    label: customDataPageExists.title || 'Custom data',
    components: customDataPageExists?.components?.map((comp) => {
      return componentsIndexed[comp.key || ''];
    }),
    input: false,
    tableView: false,
  } as unknown as FormioComponent;

  config?.components?.splice(length - 1, 0, customDataPage);
};

export const normalizeString = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};
